import { createAuthClient } from 'better-auth/react';
import { organizationClient, adminClient, anonymousClient } from 'better-auth/client/plugins';
import { stripeClient } from '@better-auth/stripe/client';
import { ssoClient } from '@better-auth/sso/client';
import { logger } from '@/lib/platform/utils/logger';

// Custom fetch function that adds Vercel bypass parameter
function customFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const bypassSecret = process.env.NEXT_PUBLIC_VERCEL_BYPASS_SECRET;

  // No bypass needed on localhost
  if (!bypassSecret) {
    return fetch(input, init);
  }

  // Safety check: only run on client-side
  if (typeof window === 'undefined') {
    logger.warn('[Better Auth customFetch] Called on server-side, skipping bypass');
    return fetch(input, init);
  }

  try {
    let url: URL;

    if (typeof input === 'string') {
      url = new URL(input, window.location.origin);
    } else if (input instanceof URL) {
      url = new URL(input.toString(), window.location.origin);
    } else if (input instanceof Request) {
      url = new URL(input.url, window.location.origin);
    } else {
      return fetch(input, init);
    }

    // Only add bypass to internal API calls
    if (url.pathname.startsWith('/api/')) {
      url.searchParams.set('x-vercel-protection-bypass', bypassSecret);

      if (input instanceof Request) {
        return fetch(new Request(url.toString(), input), init);
      } else {
        return fetch(url.toString(), init);
      }
    }

    return fetch(input, init);
  } catch (error) {
    logger.error('[Better Auth customFetch] Error:', error);
    return fetch(input, init);
  }
}

// Only use custom fetch on Preview/Production where bypass secret exists
const bypassSecret = process.env.NEXT_PUBLIC_VERCEL_BYPASS_SECRET;

export const authClient = createAuthClient({
  // Use current domain dynamically (supports valiyou.com, valiyou.io, dev.valiyou.com, dev.valiyou.io)
  // This ensures API calls go to the same domain the user is on (no CORS issues)
  baseURL:
    typeof window !== 'undefined'
      ? window.location.origin
      : process.env.NEXT_PUBLIC_BETTER_AUTH_URL || 'http://localhost:3000',
  // Only inject custom fetch if bypass secret exists (Preview/Production)
  ...(bypassSecret && {
    fetchOptions: {
      customFetchImpl: customFetch,
    },
  }),
  plugins: [
    organizationClient(),
    adminClient(),
    anonymousClient(), // Enable anonymous/demo mode login
    stripeClient({
      subscription: true, // Enable subscription management
    }),
    ssoClient(), // Enable SSO for Enterprise organizations
  ],
});

export const {
  signIn,
  signUp,
  signOut,
  useSession,
  getSession,
  updateUser,
  changePassword,
  resetPassword,
  requestPasswordReset,
  verifyEmail,
  sendVerificationEmail,
} = authClient;

// Organization functions
export const organization = authClient.organization || {
  create: async () => ({ data: null, error: 'Organization plugin not loaded' }),
  list: async () => ({ data: [], error: null }),
  setActive: async () => ({ data: null, error: null }),
  getFullOrganization: async () => ({ data: null, error: null }),
  update: async () => ({ data: null, error: null }),
  delete: async () => ({ data: null, error: null }),
  inviteMember: async () => ({ data: null, error: null }),
  acceptInvitation: async () => ({ data: null, error: null }),
  rejectInvitation: async () => ({ data: null, error: null }),
  removeMember: async () => ({ data: null, error: null }),
  updateMemberRole: async () => ({ data: null, error: null }),
};

export const {
  create: createOrganization,
  list: listOrganizations,
  setActive: setActiveOrganization,
  getFullOrganization,
  update: updateOrganization,
  delete: deleteOrganization,
  inviteMember,
  acceptInvitation,
  rejectInvitation,
  removeMember,
  updateMemberRole,
} = organization;

// Admin functions
export const { impersonateUser, stopImpersonating } = authClient.admin || {
  impersonateUser: async () => ({ data: null, error: 'Admin plugin not loaded' }),
  stopImpersonating: async () => ({ data: null, error: 'Admin plugin not loaded' }),
};

// Stripe subscription functions
export const subscription = authClient.subscription || {
  list: async () => ({ data: [], error: 'Stripe plugin not loaded' }),
  billingPortal: async () => ({ data: null, error: 'Stripe plugin not loaded' }),
  upgrade: async () => ({ data: null, error: 'Stripe plugin not loaded' }),
  cancel: async () => ({ data: null, error: 'Stripe plugin not loaded' }),
  restore: async () => ({ data: null, error: 'Stripe plugin not loaded' }),
};
